<template>
  <modal
    ref="userManagementModal"
    template="neutral"
    ref-name="userManagementModal"
    :icon-enabled="false"
    :close-button-enabled="true"
    class="user-management-modal"
  >
    <button
      v-if="currentStep !== 'userData'"
      class="back-button btn-transparent"
      @click="currentStep = 'userData'"
    >
      <md-icon>arrow_back</md-icon>
    </button>
    <button
      class="close-button btn-transparent"
      @click="closeUserManagementModal"
    >
      <md-icon>clear</md-icon>
    </button>
    <div
      class="user-management-modal__wrapper"
      :class="{ 'user-management-modal__wrapper--loading': loading }"
    >
      <spinner
        v-if="loading"
        class="loading"
      />
      <template v-else>
        <div
          v-if="currentStep === 'userData'"
          class="user-management-modal__wrapper--step1"
        >
          <h3 class="title">
            Novo membro da equipe
          </h3>
          <p class="subtitle">
            Insira as informações do novo membro da equipe do laboratório:
          </p>
          <form class="new-form-component">
            <div class="input-wrapper">
              <label
                v-if="userData.stepUser.name"
                for="name"
              >Nome completo</label>
              <md-icon class="input-icon left">
                person_outline
              </md-icon>
              <input
                id="name"
                v-model="userData.stepUser.name"
                type="text"
                placeholder="Nome completo"
                required
              >
            </div>
            <div class="input-wrapper">
              <label
                v-if="userData.stepUser.cpf"
                for="cpf"
              >CPF</label>
              <md-icon class="input-icon left">
                contact_page
              </md-icon>
              <input
                id="cpf"
                v-model="userData.stepUser.cpf"
                v-mask="'###.###.###-##'"
                type="text"
                placeholder="CPF"
                required
                @input="validateCPF"
              >
            </div>
            <span
              v-if="!validateCPF()"
              class="err"
            >*Digite um CPF válido</span>

            <UserRoleSelection
              v-model="userData.stepUser.role"
              :clear="clear"
              :can-be-super-user="false"
            />

            <div class="input-wrapper">
              <label
                v-if="userData.stepUser.username"
                for="email"
              >Email</label>
              <md-icon class="input-icon left">
                mail_outline
              </md-icon>
              <input
                id="email"
                v-model="userData.stepUser.username"
                type="email"
                placeholder="Email"
                required
                @input="validateEmail"
              >
            </div>
            <span
              v-if="!isEmailValid"
              class="err"
            >*Digite um e-mail válido</span>
            <div class="input-wrapper">
              <label
                v-if="userData.stepUser.birthday"
                for="birthday"
              >Data de nascimento</label>
              <md-icon class="input-icon left">
                event
              </md-icon>
              <input
                id="birthday"
                v-model="userData.stepUser.birthday"
                v-mask="'##/##/####'"
                type="text"
                placeholder="Data de nascimento"
                required
                @input="validateBirthday"
              >
            </div>
            <span
              v-if="!isBirthdayValid"
              class="err"
            >*Digite uma data válida</span>
            <div class="input-wrapper">
              <label
                v-if="userData.stepUser.phone"
                for="phone"
              >Telefone (Opcional)</label>
              <md-icon class="input-icon left">
                call
              </md-icon>
              <input
                id="phone"
                v-model="userData.stepUser.phone"
                v-mask="'(##) #####-####'"
                type="text"
                placeholder="Telefone (Opcional)"
              >
            </div>
            <span
              v-if="!isPhoneValid"
              class="err"
            >*Digite um telefone válido</span>
            <button
              class="md-button btn btn-primary btn-new"
              :disabled="!isFormComplete"
              @click.prevent="verifyDataBeforeSubmit"
            >
              Adicionar
            </button>
          </form>
        </div>

        <div
          v-if="currentStep === 'userWarning'"
          class="user-management-modal__wrapper--warning"
        >
          <h3 class="title">
            Aviso: Já existe um usuário pra esse CPF e E-mail!
          </h3>
          <div class="question-wrapper">
            <p class="subtitle">
              A reativação irá atualizar os dados informados e ativar novamente esse usuário no laboratório.
            </p>
            <p class="subtitle">
              Deseja reativar o usuário <strong>{{ userData.stepUser.username }}</strong>?
            </p>
          </div>
          <div class="actions">
            <button
              class="btn btn-cancel btn-primary-red"
              @click="cancel"
            >
              Cancelar
            </button>
            <button
              class="btn btn-primary btn-new"
              @click="reactivateUser"
            >
              Reativar
            </button>
          </div>
        </div>

        <div
          v-if="currentStep === 'userPassword'"
          class="user-management-modal__wrapper--step2"
        >
          <h3 class="title">
            Novo membro da equipe
          </h3>
          <p class="subtitle">
            Finalize o cadastro do novo membro definindo uma senha:
          </p>
          <form class="new-form-component">
            <div
              class="input-wrapper progress-bar"
              :class="'progress-bar--' + strengthPercentage"
            >
              <md-icon class="input-icon left">
                lock_outline
              </md-icon>
              <input
                v-if="showPassword"
                v-model="userData.stepPassword.password"
                type="text"
                placeholder="Insira a senha de acesso"
                required
              >
              <input
                v-else
                v-model="userData.stepPassword.password"
                type="password"
                placeholder="Insira a senha de acesso"
                required
              >
              <a @click.prevent="togglePassword('password')">
                <md-icon
                  v-if="showPassword"
                  class="input-icon right"
                >
                  visibility_off
                </md-icon>
                <md-icon
                  v-else
                  class="input-icon right"
                >
                  visibility
                </md-icon>
              </a>
            </div>
            <div class="password-rules">
              <h6>Força da senha:</h6>
              <ul>
                <li
                  v-for="(rule, index) in passwordRules"
                  :key="index"
                >
                  <svg-loader
                    v-if="rule.validator(userData.stepPassword.password)"
                    src="/static/images/perfil-de-acesso/checked-pass.svg"
                  />
                  <svg-loader
                    v-else
                    src="/static/images/perfil-de-acesso/wrong-pass.svg"
                  />
                  <span v-html="rule.description" />
                </li>
              </ul>
            </div>
            <div class="input-wrapper">
              <md-icon class="input-icon left">
                lock_outline
              </md-icon>
              <input
                v-if="showConfirmedPassword"
                v-model="userData.stepPassword.passwordConfirm"
                type="text"
                placeholder="Confirme a senha"
                required
              >
              <input
                v-else
                v-model="userData.stepPassword.passwordConfirm"
                type="password"
                placeholder="Confirme a senha"
                required
              >
              <a @click.prevent="togglePassword('confirmed')">
                <md-icon
                  v-if="showConfirmedPassword"
                  class="input-icon right"
                >
                  visibility_off
                </md-icon>
                <md-icon
                  v-else
                  class="input-icon right"
                >
                  visibility
                </md-icon>
              </a>
            </div>
            <span
              v-if="!isPasswordsEqual && userData.stepPassword.passwordConfirm"
              class="err"
            >
              *As senhas não coicidem
            </span>
            <button
              :disabled="!isPasswordsEqual"
              class="md-button btn btn-primary btn-new"
              @click="createUser(userData)"
            >
              Adicionar membro
            </button>
          </form>
        </div>
      </template>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isCPFValid } from '@/utils/validate-cpf'

import Spinner from '@/components/common/Spinner'
import UserRoleSelection from '@/components/ProfileManagement/components/UserRoleSelection'
import notify from '@/api/notification'
import userManagementService from '@/api/userManagement'
import { validateEmail, validatePhone, validateBirthday, passwordRules } from '@/utils/validations'



export default {
  name: 'UserManagementModal',
  components: {
    Spinner,
    UserRoleSelection
  },
  data: () => ({
    currentStep: 'userData',
    userData: {
      stepUser: {
        id: '',
        name: '',
        username: '',
        cpf: '',
        role: '',
        birthday: '',
        phone: ''
      },
      stepPassword: {
        password: '',
        passwordConfirm: ''
      }
    },
    isEmailValid: true,
    isPhoneValid: true,
    isBirthdayValid: true,
    clear: false,
    isFormComplete: false,
    loading: false,
    passwordRules,
    showPassword: false,
    showConfirmedPassword: false
  }),
  computed: {
    ...mapGetters(['teamList']),
    strengthPercentage() {
      const rulesDone = this.passwordRules.filter(rule => rule.validator(this.userData.stepPassword.password)).length
      return (rulesDone / this.passwordRules.length) * 100
    },
    isPasswordsEqual() {
      if (!this.userData.stepPassword.passwordConfirm || this.strengthPercentage !== 100) {
        return false
      }
      if (this.userData.stepPassword.password === this.userData.stepPassword.passwordConfirm) {
        return true
      }
    }
  },
  watch: {
    'userData.stepUser': {
      handler() {
        this.validateFieldsBeforeEnableButton()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['getTeamList']),
    openUserManagementModal() {
      this.$refs.userManagementModal.open()
    },
    closeUserManagementModal() {
      this.$refs.userManagementModal.close()
      this.clear = true
      this.isFormComplete = false
      this.currentStep = 'userData'
      this.loading = false
      this.clearUserObject()
      this.getTeamList()
    },
    verifyDataBeforeSubmit() {
      this.getUserStatus(this.userData.stepUser.username, this.userData.stepUser.cpf)
    },
    clearUserObject() {
      for (const prop in this.userData) {
        if (this.userData.hasOwnProperty(prop)) {
          const innerObj = this.userData[prop]
          for (const innerProp in innerObj) {
            if (innerObj.hasOwnProperty(innerProp)) {
              innerObj[innerProp] = ''
            }
          }
        }
      }
    },
    validateCPF() {
      const cpf = this.userData.stepUser.cpf
      return cpf === '' ? true : isCPFValid(cpf)
    },
    validateEmail() {
      this.isEmailValid = validateEmail(this.userData.stepUser.username)
    },
    validatePhone() {
      this.isPhoneValid = validatePhone(this.userData.stepUser.phone)
    },
    validateBirthday() {
      this.isBirthdayValid = validateBirthday(this.userData.stepUser.birthday)
      return this.isBirthdayValid
    },
    validateFieldsBeforeEnableButton() {
      const mandatoryFields = {
        name: this.userData.stepUser.name,
        username: this.userData.stepUser.username,
        cpf: this.userData.stepUser.cpf,
        role: this.userData.stepUser.role,
        birthday: this.userData.stepUser.birthday
      }
      const isMandatoryFieldsComplete = Object.values(mandatoryFields).every(
        value => value !== '' && value
      )
      this.isFormComplete =
        isMandatoryFieldsComplete &&
        this.isEmailValid &&
        isCPFValid(this.userData.stepUser.cpf) &&
        this.validateBirthday()
    },
    getUserStatus(user, cpf) {
      this.loading = true;

      return userManagementService
        .getMemberStatus(user, cpf)
        .then(res => {
          this.loading = false;
          const { status, id } = res.data;

          if (status === 'DEACTIVATED') {
            const deactivatedMember = this.teamList.find(member =>
              member.username === this.userData.stepUser.username &&
              member.cpf === this.userData.stepUser.cpf
            )

            this.userData.stepUser.id = id || deactivatedMember?.id || this.userData.stepUser.id
          }

          const statusActions = {
            'DEACTIVATED': () => this.currentStep = 'userWarning',
            'UNAVAILABLE': () => this.showNotification('Usuário já cadastrado para este laboratório!'),
            'EMAIL_ALREADY_CREATED': () => this.showNotification('O e-mail escolhido já existe, utilize outro e-mail!'),
            'CPF_ALREADY_CREATED': () => this.showNotification('O CPF escolhido já existe, utilize outro CPF!'),
            'AVAILABLE': () => this.createUser()
          };

          if (statusActions[status]) {
            statusActions[status]();
          }
        })
        .catch(err => {
          this.loading = false;
          console.error('Erro ao consultar status do usuário:', err);
          notify({
            title: 'AVISO: ',
            message: 'Não foi possível consultar a disponibilidade para o usuário'
          });
        });
    },
    showNotification(message) {
      this.currentStep = 'userData';
      notify({
        title: 'AVISO: ',
        message
      });
    },
    reactivateUser() {
      if (!this.userData.stepUser.id) {
        this.showNotification('Não foi possível identificar o usuário para reativação')
        return Promise.resolve()
      }

      this.loading = true
      return userManagementService
        .reactivateMember(this.userData)
        .then(() => {
          this.loading = false
          this.closeUserManagementModal()
          notify({
            title: 'AVISO: ',
            message: 'Usuário reativado com sucesso'
          })
        })
        .catch(err => {
          this.loading = false
          notify({
            title: 'AVISO: ',
            message: 'Não foi possível reativar o usuário'
          })
          console.error('err -->', err)
        })
    },
    cancel() {
      this.currentStep = 'userData'
    },
    passwordValidate() {
      if (!this.passwordRules || !this.userData.stepPassword.password) return
      this.passwordRules.forEach(rule => (rule.done = rule.validador(this.userData.stepPassword.password)))
    },
    togglePassword(type) {
      if (type === 'confirmed') {
        this.showConfirmedPassword = !this.showConfirmedPassword
      }
      if (type === 'password') {
        this.showPassword = !this.showPassword
      }
    },
    createUser() {
      this.loading = true
      return userManagementService
        .createMemberWithoutPassword(this.userData)
        .then(() => {
          this.loading = false
          this.closeUserManagementModal()
          notify({
            title: 'AVISO: ',
            message: 'Usuário criado com sucesso'
          })
        })
        .catch(err => {
          this.loading = false
          notify({
            title: 'AVISO: ',
            message: 'Não foi possível criar o usuário'
          })
          console.error('err -->', err)
        })
    }
  }
}
</script>
<style lang="scss">
.user-management-modal {
  font-family: 'Exo 2', sans-serif !important;

  .md-dialog {
    min-width: 500px;
    width: 90%;
    max-width: 700px;
    min-height: 400px;
    padding: 0;

    &-content {
      padding: 0;

      > div {
        height: 100%;
      }
    }

    .close-button,
    .back-button {
      i {
        color: #7f1a54;
        opacity: 1;
      }
    }

    .close-button {
      top: 13px;
      right: 28px;
    }

    .back-button {
      position: absolute;
      top: 13px;
      left: 28px;
    }
  }

  .md-dialog-title {
    display: none;
  }

  &__wrapper {
    padding: 46px 80px;
    width: 100%;
    height: 100%;

    &--loading {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &--warning {
      height: 100%;
      display: flex;
      flex-flow: column;
    }

    .title {
      font-weight: 700;
      font-size: 18px;
      color: #595959;
    }

    .subtitle {
      font-size: 14px;
      color: #363636;
      margin-top: 16px !important;
    }

    .btn-new {
      margin: 16px auto 0;
    }

    .new-form-component {
      display: flex;
      flex-direction: column;

      .err {
        font-size: 11px;
        color: red;
      }

      .password-rules {
        h6 {
          font-weight: 700;
          font-size: 13.33px;
          color: #666666;
          margin-top: 8px;
        }

        ul {
          margin-top: 4px;

          li {
            font-weight: 400;
            font-size: 12px;
            color: #363636;
            margin-top: 4px;
            display: flex;
            align-items: center;
            .md-icon {
              margin: 0;
              margin-right: 8px;
            }
          }
        }
      }
    }

    .question-wrapper {
      flex: 1;
      margin-bottom: 18px;
    }

    .actions {
      display: flex;
      justify-content: space-between;

      .btn-new {
        margin: 0;
      }
    }
  }
}
</style>
